import { CARCASS_MM } from "./carcass";
import { flapFront, mezzanineFlapDirection, type FlapFront } from "./flapFronts";
import type { PlannerProject, WardrobeModule } from "../types";
import { cornerDoorLeaves, cornerLengthMm, cornerMezzanineMm, cornerSide } from "./corner";
import { installationSpanMm, leftFillerMm, rightFillerMm, topFillerMm } from "./installation";
import {
  SHELVING_MAX_HEIGHT_MM,
  SHELVING_MAX_WIDTH_MM
} from "./shelving";
import { applyRoomSize, BACK_WALL_LENGTH_MM, createCompositionLayout, LEFT_WALL_LENGTH_MM, materializeWallOffsets, MAX_ROOM_WIDTH_MM, moduleOverlapsFloor, PERPENDICULAR_WALL_CLEARANCE_MM, placeModuleGroup } from "./layout";

export const MIN_MODULE_HEIGHT_MM = 300;
export const MAX_MODULE_HEIGHT_MM = 2750;
export const MAX_MEZZANINE_HEIGHT_MM = 1640;
/**
 * Зазор от верха мебели до потолка (приёмка 22.10): Сергей при потолке 2900
 * ставит шкаф 2890. Раньше здесь была «падуга» 44 мм — полоса под потолком в
 * 3D, которой нет в настоящей комнате. Зазор задаётся на шаге «Комната».
 */
export const DEFAULT_CEILING_GAP_MM = 10;
export const MAX_CEILING_GAP_MM = 150;
export const MAX_CABINET_SECTION_WIDTH_MM = 1200;
/** A single carcass cannot turn a room corner: its width is capped by the back wall (see maxModuleWidth). */
export const MAX_CABINET_TOTAL_WIDTH_MM = MAX_ROOM_WIDTH_MM;
export const DEFAULT_CEILING_HEIGHT_MM = 3000;
export const MIN_CEILING_HEIGHT_MM = 2000;
export const MAX_CEILING_HEIGHT_MM = 4500;
export const MAX_HINGED_DOOR_WIDTH_MM = 600;
export const MAX_SLIDING_DOOR_WIDTH_MM = 1200;
export const MAX_SLIDING_MODULE_WIDTH_MM = MAX_SLIDING_DOOR_WIDTH_MM * 2;
export const MAX_OPEN_MODULE_WIDTH_MM = 2400;
/** Антресоль не ниже 300 мм: при нехватке высоты уменьшается корпус, а не она. */
export const MIN_MEZZANINE_HEIGHT_MM = MIN_MODULE_HEIGHT_MM;
/** Обычная длина плиты под фасад: единый фасад длиннее из одной плиты не выкроить. */
export const STANDARD_FACADE_BOARD_LENGTH_MM = 2800;

/**
 * Пределы высоты антресоли при неизменной общей высоте: антресоль от 300 до
 * 1640 мм, корпус под ней — цельная стойка не выше 2750 мм и не ниже 300 мм.
 */
export const mezzanineHeightRange = (totalHeight: number) => {
  const max = Math.min(MAX_MEZZANINE_HEIGHT_MM, totalHeight - MIN_MODULE_HEIGHT_MM);
  const min = Math.max(MIN_MEZZANINE_HEIGHT_MM, totalHeight - MAX_MODULE_HEIGHT_MM);
  return { min: Math.min(min, Math.max(MIN_MEZZANINE_HEIGHT_MM, max)), max: Math.max(MIN_MEZZANINE_HEIGHT_MM, max) };
};

/**
 * Высота антресоли шкафа (приёмка 22.10): 300–1640 мм. Сначала антресоль
 * занимает свободное место до потолка, дальше корпус под ней становится ниже.
 * Шкаф уже до потолка («до потолка» или вплотную) — общая высота не меняется:
 * антресоль ниже — корпус выше, но не выше цельной стойки 2750.
 */
export const mezzanineHeightLimit = (project: PlannerProject, base: WardrobeModule, mezzanineHeight?: number) => {
  const available = usableRoomHeightMm(project) - topFillerMm(base);
  const max = Math.max(MIN_MEZZANINE_HEIGHT_MM, Math.min(MAX_MEZZANINE_HEIGHT_MM, available - MIN_MODULE_HEIGHT_MM));
  const pinned = Boolean(base.cabinetToCeiling) ||
    (mezzanineHeight !== undefined && base.height + mezzanineHeight >= available - 0.5);
  const min = pinned ? Math.max(MIN_MEZZANINE_HEIGHT_MM, available - MAX_MODULE_HEIGHT_MM) : MIN_MEZZANINE_HEIGHT_MM;
  return { min: Math.min(min, max), max, ceilingBound: available - MIN_MODULE_HEIGHT_MM < MAX_MEZZANINE_HEIGHT_MM, available, pinned };
};

/** Корпус под антресолью новой высоты: total сохраняется, если шкаф до потолка. */
export const mezzanineHeightSplit = (project: PlannerProject, base: WardrobeModule, currentMezzanine: number, requested: number) => {
  const range = mezzanineHeightLimit(project, base, currentMezzanine);
  const mezzanine = Math.max(range.min, Math.min(range.max, Math.round(requested)));
  let baseHeight = range.pinned
    ? range.available - mezzanine
    : Math.min(base.height, range.available - mezzanine);
  baseHeight = Math.max(MIN_MODULE_HEIGHT_MM, Math.min(MAX_MODULE_HEIGHT_MM, baseHeight));
  return { baseHeight, mezzanineHeight: mezzanine };
};

/** Антресоль стеллажа — от 300 до 600 мм (замечание Сергея № 27). У шкафа — до 1640. */
export const SHELVING_MAX_MEZZANINE_HEIGHT_MM = 600;

/**
 * Делит общую высоту стеллажа на нижний стеллаж (цельная стойка до 2750 мм)
 * и антресоль 300–600 мм. Антресоль старается сохранить свою высоту
 * (total − existingBase); не влезает в 300–600 — меняется нижний стеллаж.
 */
/**
 * Высота антресоли стеллажа при заданной общей высоте: 300–600 мм, а нижний
 * стеллаж — не выше 2750 мм (цельная стойка) и не ниже 300.
 */
export const shelvingMezzanineHeightRange = (totalHeight: number) => ({
  min: Math.max(MIN_MEZZANINE_HEIGHT_MM, totalHeight - SHELVING_MAX_HEIGHT_MM),
  max: Math.min(SHELVING_MAX_MEZZANINE_HEIGHT_MM, totalHeight - MIN_MODULE_HEIGHT_MM)
});

export const shelvingHeightSplit = (requestedHeight: number, existingBaseHeight?: number) => {
  const totalHeight = Math.max(
    MIN_MODULE_HEIGHT_MM,
    Math.min(SHELVING_MAX_HEIGHT_MM + SHELVING_MAX_MEZZANINE_HEIGHT_MM, Math.round(requestedHeight))
  );
  if (totalHeight <= SHELVING_MAX_HEIGHT_MM) {
    return { totalHeight, baseHeight: totalHeight, mezzanineHeight: 0 };
  }
  const preferred = existingBaseHeight === undefined
    ? totalHeight - SHELVING_MAX_HEIGHT_MM
    : totalHeight - existingBaseHeight;
  const mezzanineHeight = Math.min(
    SHELVING_MAX_MEZZANINE_HEIGHT_MM,
    Math.max(MIN_MEZZANINE_HEIGHT_MM, totalHeight - SHELVING_MAX_HEIGHT_MM, preferred)
  );
  return { totalHeight, baseHeight: totalHeight - mezzanineHeight, mezzanineHeight };
};

/** «До потолка» бывает у пеналов — одностворчатого и широкого. */
export const cabinetSupportsToCeiling = (module: Pick<WardrobeModule, "type" | "stackedOnId">) =>
  !module.stackedOnId && (module.type === "single" || module.type === "double");

/**
 * Высоты шкафа «до потолка»: корпус — цельная стойка до 2750 мм, антресоль
 * добирает остаток (300–1640 мм). Если в комнату корпус помещается целиком,
 * а антресоли ещё нет, — один корпус до потолка. null — потолок слишком низкий.
 */
export const cabinetToCeilingSplit = (availableHeight: number, hasMezzanine: boolean) => {
  const available = Math.round(availableHeight);
  if (!hasMezzanine && available <= MAX_MODULE_HEIGHT_MM) {
    return available >= MIN_MODULE_HEIGHT_MM ? { baseHeight: available, mezzanineHeight: 0 } : null;
  }
  const baseHeight = Math.min(MAX_MODULE_HEIGHT_MM, available - MIN_MEZZANINE_HEIGHT_MM);
  if (baseHeight < MIN_MODULE_HEIGHT_MM) return null;
  return { baseHeight, mezzanineHeight: Math.min(MAX_MEZZANINE_HEIGHT_MM, available - baseHeight) };
};

/** Петли на створку по её высоте: до 1500 мм — две, до 2400 — три, выше — четыре. */
export const hingeCountForDoorHeight = (heightMm: number) =>
  heightMm > 2400 ? 4 : heightMm > 1500 ? 3 : 2;

/** Доли высоты створки, на которых стоят петли (снизу вверх). */
export const hingeHeightRatios = (heightMm: number) => {
  const count = hingeCountForDoorHeight(heightMm);
  return count === 2 ? [0.28, 0.72] : count === 3 ? [0.2, 0.5, 0.8] : [0.1, 0.37, 0.63, 0.9];
};

export const cabinetHeightSplit = (
  requestedHeight: number,
  existingBaseHeight?: number
) => {
  const manufacturingLimit = existingBaseHeight === undefined
    ? MAX_MODULE_HEIGHT_MM + MAX_MEZZANINE_HEIGHT_MM
    : existingBaseHeight + MAX_MEZZANINE_HEIGHT_MM;
  const totalHeight = Math.max(
    MIN_MODULE_HEIGHT_MM,
    Math.min(
      manufacturingLimit,
      Math.round(requestedHeight)
    )
  );
  if (totalHeight <= MAX_MODULE_HEIGHT_MM) {
    return { totalHeight, baseHeight: totalHeight, mezzanineHeight: 0 };
  }
  const preferredMezzanineHeight = existingBaseHeight === undefined
    ? totalHeight - MAX_MODULE_HEIGHT_MM
    : totalHeight - existingBaseHeight;
  const mezzanineHeight = Math.min(
    MAX_MEZZANINE_HEIGHT_MM,
    Math.max(MIN_MODULE_HEIGHT_MM, preferredMezzanineHeight)
  );
  return {
    totalHeight,
    baseHeight: totalHeight - mezzanineHeight,
    mezzanineHeight
  };
};

export const cabinetSectionWidths = (requestedWidth: number) => {
  const totalWidth = Math.max(300, Math.round(requestedWidth));
  const count = Math.max(
    1,
    Math.ceil(totalWidth / MAX_CABINET_SECTION_WIDTH_MM)
  );
  const baseWidth = Math.floor(totalWidth / count);
  let remainder = totalWidth - baseWidth * count;
  return Array.from({ length: count }, () =>
    baseWidth + (remainder-- > 0 ? 1 : 0)
  );
};

/** Local X coordinates of the shared structural uprights inside one wide carcass. */
export const cabinetDividerOffsets = (module: WardrobeModule) => {
  if (
    module.type === "shelving" ||
    module.type === "end" ||
    module.type === "corner45"
  ) return [];
  const widths = cabinetSectionWidths(module.width);
  let cursor = -module.width / 2;
  return widths.slice(0, -1).map((width) => {
    cursor += width;
    return cursor;
  });
};

export type DoorHingeApplication = "full-overlay" | "half-overlay";

export type HingedDoorLeafLayout = {
  index: number;
  sectionIndex: number;
  sectionLeafIndex: number;
  centerMm: number;
  widthMm: number;
  hingeSide: -1 | 1;
  hingeApplication: DoorHingeApplication;
  /** Centre line of the side panel or shared upright carrying the mounting plate. */
  supportOffsetMm: number;
};

/**
 * Production layout for hinged fronts. Each structural bay owns its leaves.
 * Doors at the outside of the carcass use full-overlay hinges; leaves mounted
 * to a shared upright use half-overlay (adjacent-door / twin-application)
 * hinges on the two opposite faces of that one carcass upright.
 */
export const hingedDoorLeafLayout = (
  module: WardrobeModule
): HingedDoorLeafLayout[] => {
  // Угловой (Г-образный, 16.10): по двери на корпус, у каждой своя середина.
  if (module.type === "corner45") {
    return module.door === "hinged" ? cornerDoorLeaves(module) : [];
  }
  if (module.door !== "hinged" || module.type === "shelving" || module.type === "end") {
    return [];
  }
  // Антресоль с откидными фасадами — без распашных дверей.
  if (mezzanineFlapLayout(module)) return [];

  const sectionWidths = cabinetSectionWidths(module.width);
  const sectionCount = sectionWidths.length;
  const outerRevealMm = 2;
  const betweenLeavesRevealMm = 2;
  const panelThicknessMm = CARCASS_MM;
  let sectionStartMm = -module.width / 2;
  let doorIndex = 0;

  return sectionWidths.flatMap((sectionWidthMm, sectionIndex) => {
    const leavesInSection =
      sectionCount === 1 && module.type === "double"
        ? 2
        : sectionWidthMm > MAX_HINGED_DOOR_WIDTH_MM
          ? 2
          : 1;
    const leftInsetMm = sectionIndex === 0
      ? outerRevealMm
      : betweenLeavesRevealMm / 2;
    const rightInsetMm = sectionIndex === sectionCount - 1
      ? outerRevealMm
      : betweenLeavesRevealMm / 2;
    const widthMm = Math.max(
      1,
      (sectionWidthMm -
        leftInsetMm -
        rightInsetMm -
        betweenLeavesRevealMm * (leavesInSection - 1)) /
        leavesInSection
    );
    const sectionEndMm = sectionStartMm + sectionWidthMm;

    const leaves = Array.from({ length: leavesInSection }, (_, sectionLeafIndex) => {
      let hingeSide: -1 | 1;
      if (leavesInSection === 2) {
        hingeSide = sectionLeafIndex === 0 ? -1 : 1;
      } else if (sectionCount === 1) {
        hingeSide = module.doorHinge === "right" ? 1 : -1;
      } else if (sectionIndex === 0) {
        hingeSide = -1;
      } else if (sectionIndex === sectionCount - 1) {
        hingeSide = 1;
      } else {
        hingeSide = sectionIndex % 2 === 0 ? -1 : 1;
      }

      const usesOuterSide =
        (hingeSide === -1 && sectionIndex === 0) ||
        (hingeSide === 1 && sectionIndex === sectionCount - 1);
      const centerMm =
        sectionStartMm +
        leftInsetMm +
        widthMm / 2 +
        sectionLeafIndex * (widthMm + betweenLeavesRevealMm);
      const supportOffsetMm = hingeSide === -1
        ? sectionIndex === 0
          ? -module.width / 2 + panelThicknessMm / 2
          : sectionStartMm
        : sectionIndex === sectionCount - 1
          ? module.width / 2 - panelThicknessMm / 2
          : sectionEndMm;

      const leaf: HingedDoorLeafLayout = {
        index: doorIndex,
        sectionIndex,
        sectionLeafIndex,
        centerMm,
        widthMm,
        hingeSide,
        hingeApplication: usesOuterSide ? "full-overlay" : "half-overlay",
        supportOffsetMm
      };
      doorIndex += 1;
      return leaf;
    });

    sectionStartMm = sectionEndMm;
    return leaves;
  });
};

export type MezzanineFlapLayout = {
  index: number;
  sectionIndex: number;
  centerMm: number;
  widthMm: number;
  heightMm: number;
  front: FlapFront;
};

/** Высота откидного фасада антресоли: высота антресоли минус зазоры. */
export const mezzanineFlapHeightMm = (module: Pick<WardrobeModule, "height">) => Math.max(220, module.height - 8);

/**
 * Антресоль с откидными фасадами (09.10): в каждом пролёте один фасад на всю
 * ширину пролёта, вверх на подъёмнике или вниз. null — обычные двери (не
 * выбрано или механизм не подходит по высоте антресоли).
 */
export const mezzanineFlapLayout = (
  module: WardrobeModule,
  /** Место над антресолью до потолка (moduleHeadroomMm): от него зависит, подойдут ли HL и HS. */
  headroomMm = Infinity
): MezzanineFlapLayout[] | null => {
  const direction = mezzanineFlapDirection(module);
  if (!direction) return null;
  const heightMm = mezzanineFlapHeightMm(module);
  const front = flapFront(direction, module.doorLift, heightMm, headroomMm);
  if (!front) return null;
  const sectionWidths = cabinetSectionWidths(module.width);
  const outerRevealMm = 2;
  const betweenRevealMm = 2;
  let start = -module.width / 2;
  return sectionWidths.map((sectionWidthMm, sectionIndex) => {
    const left = sectionIndex === 0 ? outerRevealMm : betweenRevealMm / 2;
    const right = sectionIndex === sectionWidths.length - 1 ? outerRevealMm : betweenRevealMm / 2;
    const widthMm = Math.max(1, sectionWidthMm - left - right);
    const centerMm = start + left + widthMm / 2;
    start += sectionWidthMm;
    return { index: sectionIndex, sectionIndex, centerMm, widthMm, heightMm, front };
  });
};

export const cabinetRunModules = (
  project: PlannerProject,
  module: WardrobeModule | undefined
) => {
  const base = baseModuleFor(project, module);
  if (!base || base.type === "shelving" || base.type === "end" || base.type === "corner45") return [];
  if (!base.cabinetRunId) return [base];
  return project.modules.filter(candidate =>
    candidate.type !== "mezzanine" &&
    candidate.cabinetRunId === base.cabinetRunId
  );
};

/**
 * Сколько шкаф может занять по своей стене, не уезжая за угол: стена минус
 * остальные изделия у неё, зазоры у стен и доборы самого шкафа (приёмка 04.10,
 * wb-04). Шкаф у боковой стены считается по её длине — раньше по задней, и
 * широкий шкаф на боковой стене вылезал за комнату (проверка 07.10).
 */
export const maxCabinetRunWidthOnBackWall = (
  project: PlannerProject,
  module: WardrobeModule | undefined
) => {
  applyRoomSize(project);
  const run = cabinetRunModules(project, module);
  if (!run.length) return BACK_WALL_LENGTH_MM;
  const runIds = new Set(run.map(candidate => candidate.id));
  const layout = createCompositionLayout(project);
  const wall = layout.placements.find(placement => placement.module.id === run[0]!.id && placement.wing === 0)?.wall ?? "back";
  const wallLength = wall === "back" ? BACK_WALL_LENGTH_MM : LEFT_WALL_LENGTH_MM;
  const others = layout.placements
    .filter(placement => placement.wall === wall &&
      !placement.module.stackedOnId && !runIds.has(placement.module.id))
    .reduce((sum, placement) => sum + (placement.wing === 1 ? placement.module.width : installationSpanMm(placement.module)), 0);
  const fillerLeft = leftFillerMm(run[0]!);
  const fillerRight = rightFillerMm(run.at(-1)!);
  // У задней стены зазор 40 мм у обеих боковых стен (добор его заменяет),
  // у боковой — только в углу: спереди комната открыта.
  const clearances = wall === "back"
    ? Math.max(fillerLeft, PERPENDICULAR_WALL_CLEARANCE_MM) + Math.max(fillerRight, PERPENDICULAR_WALL_CLEARANCE_MM)
    : fillerLeft + fillerRight + PERPENDICULAR_WALL_CLEARANCE_MM;
  const upper = Math.max(MIN_MODULE_WIDTH_FOR_RUN_MM, Math.floor(wallLength - others - clearances - 2));
  if (run.length > 1) return upper;
  // Свободное место у стены бывает разорвано соседями и угловыми шкафами:
  // ищем самую большую ширину, при которой шкафу с торцами есть куда встать
  // рядом с нынешним местом (проверка 07.10).
  const base = run[0]!;
  const start = materializeWallOffsets(project).get(base.id) ?? 0;
  const fits = (width: number) => placeModuleGroup(
    { ...project, modules: project.modules.map(candidate => candidate.id === base.id ? { ...candidate, width } : candidate) },
    base.id, start, 0
  ) !== null;
  if (fits(upper)) return upper;
  let low = Math.min(base.width, upper);
  if (!fits(low)) return Math.max(MIN_MODULE_WIDTH_FOR_RUN_MM, low);
  let high = upper;
  while (high - low > 10) {
    const middle = Math.round((low + high) / 2);
    if (fits(middle)) low = middle; else high = middle;
  }
  applyRoomSize(project);
  return low;
};
const MIN_MODULE_WIDTH_FOR_RUN_MM = 300;

export const cabinetRunWidth = (
  project: PlannerProject,
  module: WardrobeModule | undefined
) => cabinetRunModules(project, module).reduce((sum, candidate) => sum + candidate.width, 0);

export const cabinetTotalHeight = (
  project: PlannerProject,
  module: WardrobeModule | undefined
) => {
  const base = baseModuleFor(project, module);
  return base ? base.height + (mezzanineFor(project, base.id)?.height ?? 0) : 0;
};

export const shelvingUpperFor = (
  project: PlannerProject,
  baseModuleId: string
) =>
  project.modules.find(
    (module) =>
      module.type === "shelving" && module.stackedOnId === baseModuleId
  );

export const shelvingTotalHeight = (
  project: PlannerProject,
  module: WardrobeModule | undefined
) => {
  const base = baseModuleFor(project, module);
  if (!base || base.type !== "shelving") return 0;
  return base.height + (shelvingUpperFor(project, base.id)?.height ?? 0);
};

export const maxShelvingTotalHeight = (
  project: PlannerProject,
  module: WardrobeModule
) => {
  const base = baseModuleFor(project, module) ?? module;
  // Нижний стеллаж при нехватке растёт сам, поэтому предел — 2750 + 600.
  return Math.max(
    MIN_MODULE_HEIGHT_MM,
    Math.min(
      SHELVING_MAX_HEIGHT_MM + SHELVING_MAX_MEZZANINE_HEIGHT_MM,
      usableRoomHeightMm(project) - topFillerMm(base)
    )
  );
};

export const maxCabinetTotalHeight = (
  project: PlannerProject,
  module: WardrobeModule
) => {
  const base = baseModuleFor(project, module) ?? module;
  const lowerHeight = mezzanineFor(project, base.id)
    ? base.height
    : MAX_MODULE_HEIGHT_MM;
  return Math.max(
    MIN_MODULE_HEIGHT_MM,
    Math.min(
      lowerHeight + MAX_MEZZANINE_HEIGHT_MM,
      usableRoomHeightMm(project) - topFillerMm(base)
    )
  );
};

export const doorLeafCount = (module: WardrobeModule) => {
  if (module.type === "shelving") return 0;
  if (module.door === "open") return 0;
  if (module.door === "sliding") return 2;
  return mezzanineFlapLayout(module)?.length ?? hingedDoorLeafLayout(module).length;
};

export const maxModuleWidth = (module: WardrobeModule) => {
  // Угловой: длина считается из глухой части, добора и двери (28.10).
  if (module.type === "corner45") return Math.max(module.width, cornerLengthMm(module));
  if (module.type === "shelving") return SHELVING_MAX_WIDTH_MM;
  if (module.door === "sliding") return MAX_SLIDING_MODULE_WIDTH_MM;
  if (
    module.type === "single" ||
    module.type === "double" ||
    module.type === "mezzanine"
  ) return MAX_CABINET_TOTAL_WIDTH_MM;
  if (module.door === "open") return MAX_OPEN_MODULE_WIDTH_MM;
  return doorLeafCount(module) * MAX_HINGED_DOOR_WIDTH_MM;
};

export const baseModuleFor = (
  project: PlannerProject,
  module: WardrobeModule | undefined
) => {
  if (!module) return undefined;
  if (!module.stackedOnId) return module;
  return project.modules.find((candidate) => candidate.id === module.stackedOnId);
};

/**
 * Единый фасад: распашные створки корпуса продолжаются на антресоль, у самой
 * антресоли своих дверей нет. Выбор хранится у корпуса.
 */
export const hasUnifiedFacade = (project: PlannerProject, base: WardrobeModule | undefined) =>
  Boolean(base && base.type !== "mezzanine" && base.door === "hinged" &&
    base.mezzanineFacade === "unified" && mezzanineFor(project, base.id));

/** Высота единого фасада: от цоколя до верха антресоли минус зазоры, как у обычной створки. */
export const unifiedFacadeHeightMm = (project: PlannerProject, base: WardrobeModule, plinthMm: number) =>
  base.height + (mezzanineFor(project, base.id)?.height ?? 0) - plinthMm - 8;

export const mezzanineFor = (
  project: PlannerProject,
  baseModuleId: string
) =>
  project.modules.find(
    (module) =>
      module.type === "mezzanine" && module.stackedOnId === baseModuleId
  );

export const moduleElevationMm = (
  project: PlannerProject,
  module: WardrobeModule
) => {
  if (!module.stackedOnId) return 0;
  return baseModuleFor(project, module)?.height ?? 0;
};

export const moduleTopMm = (
  project: PlannerProject,
  module: WardrobeModule
) => moduleElevationMm(project, module) + module.height + cornerMezzanineMm(module) + topFillerMm(module);

export const ceilingHeightMm = (project: PlannerProject) =>
  Math.max(
    MIN_CEILING_HEIGHT_MM,
    Math.min(
      MAX_CEILING_HEIGHT_MM,
      Math.round(project.room.ceilingHeight ?? DEFAULT_CEILING_HEIGHT_MM)
    )
  );

export const ceilingGapMm = (project: Pick<PlannerProject, "room">) => {
  const value = Number(project.room.ceilingGap);
  return Number.isFinite(value) ? Math.max(0, Math.min(MAX_CEILING_GAP_MM, Math.round(value))) : DEFAULT_CEILING_GAP_MM;
};

/** Самый высокий верх мебели: потолок минус зазор. */
export const usableRoomHeightMm = (project: PlannerProject) =>
  ceilingHeightMm(project) - ceilingGapMm(project);

export const maxModuleHeight = (
  project: PlannerProject,
  module: WardrobeModule
) => {
  const ceilingHeight = usableRoomHeightMm(project);
  const topFiller = topFillerMm(module);
  if (module.type === "shelving") {
    if (module.stackedOnId) {
      const base = baseModuleFor(project, module);
      return Math.max(
        MIN_MODULE_HEIGHT_MM,
        Math.min(
          SHELVING_MAX_MEZZANINE_HEIGHT_MM,
          ceilingHeight - (base?.height ?? 0) - topFiller
        )
      );
    }
    return Math.max(
      MIN_MODULE_HEIGHT_MM,
      Math.min(SHELVING_MAX_HEIGHT_MM, ceilingHeight - topFiller)
    );
  }
  if (module.type === "mezzanine") {
    const base = baseModuleFor(project, module);
    return Math.max(
      MIN_MODULE_HEIGHT_MM,
      Math.min(
        MAX_MEZZANINE_HEIGHT_MM,
        ceilingHeight - (base?.height ?? 0) - topFiller
      )
    );
  }
  const mezzanine = mezzanineFor(project, module.id);
  return Math.max(
    MIN_MODULE_HEIGHT_MM,
    Math.min(
      MAX_MODULE_HEIGHT_MM,
      ceilingHeight - (mezzanine?.height ?? 0) - cornerMezzanineMm(module) - topFiller
    )
  );
};

/**
 * Почему угловой шкаф с этой стороны сейчас не поставить; null — можно.
 * Приоритет у того, что уже стоит: если угол занят шкафом или торцом, угловой
 * шкаф не ставится, а не сдвигает и не прячет соседей (приёмка 07.10, wb-24).
 */
export const cornerAddBlocker = (
  project: PlannerProject,
  side: "left" | "right",
  /** Уже стоящий угловой, который переносят в другой угол: его размеры и он сам не мешает. */
  moving?: WardrobeModule
): string | null => {
  if (project.modules.some(module => module.id !== moving?.id && module.type === "corner45" && cornerSide(module) === side)) {
    return side === "left" ? "левый угловой уже стоит" : "правый угловой уже стоит";
  }
  const rest = { ...project, modules: project.modules.filter(module => module.id !== moving?.id) };
  const offsets = materializeWallOffsets(rest);
  const probe: WardrobeModule = moving
    ? { ...moving, id: "corner-probe", cornerSide: side, wallOffset: undefined }
    : {
        id: "corner-probe", type: "corner45", width: 1000, cornerReturnWidth: 1000, cornerSide: side,
        height: 2360, depth: 400, door: "hinged", facade: "wood", handle: "bar", items: []
      };
  const modules = rest.modules.map(module => ({ ...module, wallOffset: offsets.get(module.id) ?? module.wallOffset }));
  return moduleOverlapsFloor({ ...project, modules: [...modules, probe] }, probe.id)
    ? "угол занят — отодвиньте шкаф или торец от угла"
    : null;
};
